import { Webhook, CheckCircle, Activity, TrendingUp, Globe, Zap, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function APIsWebhooksPage() {

  // Mock API & Webhook integrations data
  const apiIntegrations = [
    {
      id: 1,
      name: 'Public REST API',
      type: 'API',
      status: 'active',
      endpoint: 'https://api.line4.ai/v1',
      requestsToday: 12847,
      avgResponseTime: '145ms',
      uptime: '99.98%',
      lastRequest: '1 second ago',
      icon: Globe,
      color: 'text-blue-400'
    },
    {
      id: 2,
      name: 'Risk Webhooks',
      type: 'Webhook',
      status: 'active',
      endpoint: 'https://webhooks.line4.ai/risks',
      eventsToday: 234,
      avgResponseTime: '89ms',
      uptime: '99.95%',
      lastEvent: '5 minutes ago',
      icon: Webhook,
      color: 'text-purple-400'
    },
    {
      id: 3,
      name: 'Compliance Webhooks',
      type: 'Webhook',
      status: 'active',
      endpoint: 'https://webhooks.line4.ai/compliance',
      eventsToday: 156,
      avgResponseTime: '102ms',
      uptime: '100%',
      lastEvent: '12 minutes ago',
      icon: Webhook,
      color: 'text-green-400'
    },
    {
      id: 4,
      name: 'Internal API',
      type: 'API',
      status: 'active',
      endpoint: 'https://internal-api.line4.ai/v2',
      requestsToday: 45623,
      avgResponseTime: '67ms',
      uptime: '99.99%',
      lastRequest: '< 1 second ago',
      icon: Zap,
      color: 'text-yellow-400'
    }
  ];

  const totalRequests = apiIntegrations.reduce((sum, i) => 
    sum + (i.requestsToday || i.eventsToday || 0), 0
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-purple-500/20">
            <Webhook className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">APIs & Webhooks</h1>
            <p className="text-neutral-400">REST APIs and webhook integrations for external systems</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-outline">View API Docs</button>
          <button className="btn-primary">Create Webhook</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Requests (24h)</p>
              <p className="text-2xl font-bold text-white mt-1">{totalRequests.toLocaleString()}</p>
            </div>
            <Activity className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Active Endpoints</p>
              <p className="text-2xl font-bold text-white mt-1">{apiIntegrations.length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Avg Response Time</p>
              <p className="text-2xl font-bold text-white mt-1">101ms</p>
            </div>
            <TrendingUp className="w-8 h-8 text-cyan-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">System Uptime</p>
              <p className="text-2xl font-bold text-white mt-1">99.97%</p>
            </div>
            <Zap className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* API Configuration */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">API Configuration</h2>
        <div className="space-y-4">
          <div>
            <label className="form-label">API Key</label>
            <div className="flex gap-2">
              <input 
                type="password" 
                className="form-input flex-1" 
                value="sk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                readOnly
              />
              <button className="btn-outline">Regenerate</button>
              <button className="btn-outline">Copy</button>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Keep your API key secure. Never share it publicly.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Rate Limiting</label>
              <select className="form-input w-full">
                <option>1000 requests/minute</option>
                <option selected>5000 requests/minute</option>
                <option>10000 requests/minute</option>
                <option>Unlimited (Enterprise)</option>
              </select>
            </div>
            <div>
              <label className="form-label">API Version</label>
              <select className="form-input w-full">
                <option>v1 (Legacy)</option>
                <option selected>v2 (Current)</option>
                <option>v3 (Beta)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Allowed IP Addresses</label>
            <textarea 
              className="form-input w-full" 
              rows={3}
              placeholder="Enter IP addresses (one per line)&#10;Example:&#10;192.168.1.1&#10;10.0.0.0/8"
            />
            <p className="text-xs text-neutral-500 mt-1">Leave empty to allow all IPs (not recommended for production)</p>
          </div>
        </div>
      </div>

      {/* Active Integrations */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Active Endpoints</h2>
          <div className="flex gap-2">
            <button className="btn-outline text-sm">Filter</button>
            <button className="btn-outline text-sm">Export Logs</button>
          </div>
        </div>

        <div className="space-y-4">
          {apiIntegrations.map((integration) => {
            const Icon = integration.icon;
            const isWebhook = integration.type === 'Webhook';
            return (
              <div
                key={integration.id}
                className="bg-neutral-900 rounded-lg p-4 border border-neutral-800 hover:border-neutral-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-neutral-800 rounded-lg">
                      <Icon className={cn("w-6 h-6", integration.color)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold">{integration.name}</h3>
                        <span className="badge badge-success">{integration.status}</span>
                        <span className="badge bg-neutral-800 text-neutral-300 border border-neutral-700">
                          {integration.type}
                        </span>
                      </div>
                      <div className="mb-3">
                        <p className="text-xs text-neutral-500">Endpoint</p>
                        <code className="text-sm text-cyan-400 bg-neutral-800/50 px-2 py-1 rounded">
                          {integration.endpoint}
                        </code>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-neutral-500">
                            {isWebhook ? 'Events (24h)' : 'Requests (24h)'}
                          </p>
                          <p className="text-lg font-semibold text-white">
                            {(integration.requestsToday || integration.eventsToday)?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Avg Response</p>
                          <p className="text-lg font-semibold text-green-400">{integration.avgResponseTime}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Uptime</p>
                          <p className="text-lg font-semibold text-cyan-400">{integration.uptime}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Last {isWebhook ? 'Event' : 'Request'}</p>
                          <p className="text-sm text-neutral-300 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {integration.lastRequest || integration.lastEvent}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-outline text-xs px-3 py-1">View Logs</button>
                    <button className="btn-outline text-xs px-3 py-1">Configure</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Recent API Activity</h2>
        <div className="space-y-3">
          {[
            { method: 'POST', endpoint: '/api/v2/risks', status: 200, time: '2s ago', client: 'Slack Integration' },
            { method: 'GET', endpoint: '/api/v2/compliance/frameworks', status: 200, time: '5s ago', client: 'Dashboard UI' },
            { method: 'POST', endpoint: '/webhooks/risks', status: 201, time: '12s ago', client: 'External System' },
            { method: 'GET', endpoint: '/api/v1/audit-logs', status: 200, time: '18s ago', client: 'Mobile App' },
            { method: 'PUT', endpoint: '/api/v2/controls/CTR-001', status: 200, time: '25s ago', client: 'Admin Portal' },
            { method: 'POST', endpoint: '/webhooks/compliance', status: 500, time: '32s ago', client: 'Monitoring Bot' }
          ].map((activity, idx) => (
            <div key={idx} className="bg-neutral-900 rounded-lg p-3 border border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <span className={cn(
                  "badge font-mono text-xs",
                  activity.method === 'GET' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                  activity.method === 'POST' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                )}>
                  {activity.method}
                </span>
                <code className="text-sm text-neutral-300 flex-1">{activity.endpoint}</code>
                <span className={cn(
                  "badge text-xs font-mono",
                  activity.status === 200 || activity.status === 201 ? 'badge-success' : 'badge-danger'
                )}>
                  {activity.status}
                </span>
                <span className="text-sm text-neutral-500">{activity.client}</span>
                <span className="text-sm text-neutral-500">{activity.time}</span>
              </div>
              {activity.status >= 400 && (
                <AlertCircle className="w-4 h-4 text-red-400 ml-3" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
